import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Trophy, Check, Brain, MapPin, Camera, Search, Timer, Dumbbell, ChevronDown, Cloud, Monitor, Square, Share, PlusSquare, Info, Image as ImageIcon } from 'lucide-react'
import ExerciseSelector from './ExerciseSelector'
import BodyPartRadarChart from './BodyPartRadarChart'
import Calendar from './Calendar'
import PeriodComparisonChart from './PeriodComparisonChart'
import Footer from './Footer'
import DailyVolumeChart from './DailyVolumeChart'
import LiveTracker from './LiveTracker'


const LandingPage = () => {
  const { t, i18n } = useTranslation()
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const utmData = {}
    ;['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
      const val = params.get(key)
      if (val) utmData[key] = val
    })
    if (Object.keys(utmData).length > 0) {
      localStorage.setItem('utm_params', JSON.stringify(utmData))
    }
  }, [])
  const [logs, setLogs] = useState([])
  const [exercises, setExercises] = useState([])
  const [exercise, setExercise] = useState('')
  const [weight, setWeight] = useState('')
  const [reps, setReps] = useState('')
  const [sets, setSets] = useState('')
  const [timeMin, setTimeMin] = useState('')
  const [timeSec, setTimeSec] = useState('')
  const [distance, setDistance] = useState('')
  const [floors, setFloors] = useState('')
  const [calories, setCalories] = useState('')

  const [isBodyweight, setIsBodyweight] = useState(false)

  // Plank timer
  const [isPlankTimerRunning, setIsPlankTimerRunning] = useState(false)
  const [plankElapsed, setPlankElapsed] = useState(0)
  const [plankWeightError, setPlankWeightError] = useState(false)
  const plankTimerRef = useRef(null)
  const plankWakeLockRef = useRef(null)

  const [date, setDate] = useState(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })

  // Filter state
  const [selectedBodyPart, setSelectedBodyPart] = useState('脚')
  const [comparisonPeriod, setComparisonPeriod] = useState('week')

  const JP_TO_KEY = {
    '脚': 'legs', '腕': 'arms', '肩': 'shoulders', '背中': 'back', '胸': 'chest', '腹筋': 'abs', '有酸素運動': 'cardio'
  }
  const getBodyPartLabel = (jpName) => {
    const key = JP_TO_KEY[jpName]
    return key ? t(`body_parts.${key}`) : jpName
  }

  const filteredExercises = exercises.filter(e => e.body_part === selectedBodyPart)

  const formattedDate = date.replace(/-/g, '/')
  const dailyLogs = logs.filter(log => log.date === formattedDate)

  const selectedExercise = exercises.find(e => e.name === exercise || e.en_name === exercise)
  const isCardio = selectedExercise?.body_part === '有酸素運動'
  const isRunning = isCardio && (selectedExercise?.name === 'ランニング' || selectedExercise?.en_name === 'Running')
  const isStairClimber = isCardio && (selectedExercise?.name === 'ステアクライマー' || selectedExercise?.en_name === 'Stair Climber')
  const isPlank = !isCardio && (selectedExercise?.name === 'プランク' || selectedExercise?.en_name === 'Plank')

  // Transform flat local logs to API-like structure for charts
  const apiLogs = logs.map(log => {
    const exerciseObj = exercises.find(e => e.name === log.exercise || e.en_name === log.exercise)
    const setBodyPart = exerciseObj ? exerciseObj.body_part : null
    const setCount = parseInt(log.sets) || 0

    // Standardize date to YYYY-MM-DD for charts/calendar
    const standardDate = log.date.replace(/\//g, '-')

    return {
      id: log.id,
      date: standardDate, // YYYY-MM-DD
      workout_sets: [{
        exercise: {
          name: log.exercise,
          body_part: setBodyPart
        },
        sets: setCount,
        // Map local fields to set fields for potential future use (though charts mostly use body_part)
        weight: log.weight,
        reps: log.reps,
        time: log.time,
        distance: log.distance,
        floors: log.floors,
        calories: log.calories
      }]
    }
  })

  // Filter for Radar Chart (uses same structure now)
  const chartLogs = dailyLogs.map(log => {
    const exerciseObj = exercises.find(e => e.name === log.exercise || e.en_name === log.exercise)
    const setBodyPart = exerciseObj ? exerciseObj.body_part : null
    const setCount = parseInt(log.sets) || 0

    return {
      workout_sets: [{
        exercise: {
          body_part: setBodyPart
        },
        sets: setCount
      }]
    }
  })

  useEffect(() => {
    const saved = localStorage.getItem('gym_demo_logs')
    if (saved) {
      setLogs(JSON.parse(saved))
    }

    // Fetch exercises for selector
    fetch('/api/v1/exercises')
      .then(res => res.json())
      .then(data => setExercises(data))
      .catch(err => console.error(err))
  }, [])

  // 種目切り替え時にプランクタイマーを停止
  useEffect(() => {
    if (plankTimerRef.current) { clearInterval(plankTimerRef.current); plankTimerRef.current = null }
    if (plankWakeLockRef.current) { plankWakeLockRef.current.release(); plankWakeLockRef.current = null }
    setIsPlankTimerRunning(false)
    setPlankElapsed(0)
    setIsBodyweight(false)
    setPlankWeightError(false)
  }, [exercise])

  useEffect(() => {
    if (!isPlankTimerRunning) return
    plankTimerRef.current = setInterval(() => setPlankElapsed(prev => prev + 1), 1000)
    return () => clearInterval(plankTimerRef.current)
  }, [isPlankTimerRunning])

  const handlePlankStart = () => {
    if (!isBodyweight && (!weight || parseFloat(weight) <= 0)) {
      setPlankWeightError(true)
      return
    }
    setPlankWeightError(false)
    setPlankElapsed(0)
    setIsPlankTimerRunning(true)
    if ('wakeLock' in navigator) {
      navigator.wakeLock.request('screen').then(lock => { plankWakeLockRef.current = lock }).catch(() => {})
    }
  }

  const handlePlankStop = () => {
    clearInterval(plankTimerRef.current)
    plankTimerRef.current = null
    if (plankWakeLockRef.current) { plankWakeLockRef.current.release(); plankWakeLockRef.current = null }
    setIsPlankTimerRunning(false)
    const totalSecs = plankElapsed
    if (totalSecs === 0) return
    const newLog = {
      id: Date.now(),
      exercise,
      weight: isBodyweight ? null : (weight ? parseFloat(weight) : null),
      is_bodyweight: isBodyweight,
      reps: null,
      sets: 1,
      time: totalSecs / 60,
      distance: null,
      floors: null,
      calories: null,
      date: date.replace(/-/g, '/')
    }
    const updatedLogs = [newLog, ...logs]
    setLogs(updatedLogs)
    localStorage.setItem('gym_demo_logs', JSON.stringify(updatedLogs))
    setExercise('')
    setWeight('')
    setIsBodyweight(false)
    setPlankElapsed(0)
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const savedExerciseObj = exercises.find(e => e.name === exercise || e.en_name === exercise)
    const isCardio = savedExerciseObj && savedExerciseObj.body_part === '有酸素運動'
    const isStairClimber = isCardio && (savedExerciseObj?.name === 'ステアクライマー' || savedExerciseObj?.en_name === 'Stair Climber')
    const isPlankSubmit = !isCardio && (savedExerciseObj?.name === 'プランク' || savedExerciseObj?.en_name === 'Plank')

    if (!exercise) return
    if ((isCardio && !isStairClimber && !timeMin && !timeSec && !distance && !calories) || (isStairClimber && !timeMin && !timeSec && !floors && !calories)) return
    if (!isCardio && !isPlankSubmit && !sets) return

    const newLog = {
      id: Date.now(),
      exercise,
      weight: isCardio ? null : (isBodyweight ? null : weight),
      is_bodyweight: !isCardio && isBodyweight,
      reps: (isCardio || isPlankSubmit) ? null : reps,
      sets: (isCardio || isPlankSubmit) ? 1 : sets,
      time: (isCardio || isPlankSubmit) && (timeMin || timeSec) ? (parseInt(timeMin || 0) + (parseInt(timeSec || 0) / 60)) : null,
      distance: isCardio && distance ? parseFloat(distance) : null,
      floors: isCardio && isStairClimber && floors ? parseInt(floors) : null,
      calories: isCardio ? calories : null,
      date: date.replace(/-/g, '/')
    }

    const updatedLogs = [newLog, ...logs]
    setLogs(updatedLogs)
    localStorage.setItem('gym_demo_logs', JSON.stringify(updatedLogs))

    setExercise('')
    setWeight('')
    setIsBodyweight(false)
    setReps('')
    setSets('')
    setTimeMin('')
    setTimeSec('')
    setDistance('')
    setFloors('')
    setCalories('')
  }

  const handleSaveRun = (runData) => {
    if (!exercise) return

    const newLog = {
      id: Date.now(),
      exercise,
      weight: null,
      reps: null,
      sets: 1,
      time: runData.duration / 60,
      distance: parseFloat(runData.distance),
      floors: null,
      calories: runData.calories,
      date: date.replace(/-/g, '/')
    }

    const updatedLogs = [newLog, ...logs]
    setLogs(updatedLogs)
    localStorage.setItem('gym_demo_logs', JSON.stringify(updatedLogs))
    setExercise('')
    alert(t('success_saved'))
  }

  const deleteLog = (id) => {
    const updatedLogs = logs.filter(log => log.id !== id)
    setLogs(updatedLogs)
    localStorage.setItem('gym_demo_logs', JSON.stringify(updatedLogs))
  }

  return (
    <div className="max-w-2xl mx-auto px-4 pt-2 pb-4">

      {/* ===== HERO SECTION ===== */}
      <section className="text-center bg-[#1c1c1e] pt-5 sm:pt-7 px-5 sm:px-7 pb-3 sm:pb-4 rounded-2xl border border-gray-800 mb-4 mt-2">
        <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/30 rounded-full px-4 py-1.5 mb-3">
          <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
          <span className="text-orange-400 text-sm font-bold">{t('landing.users_count')}</span>
        </div>
        <h1 className="text-[22px] sm:text-2xl md:text-3xl font-bold text-white mb-4 leading-tight whitespace-pre-line">
          {t('landing.hero_title')}
        </h1>
        <p className="text-gray-400 text-lg leading-relaxed whitespace-pre-wrap mb-4">
          {t('landing.hero_subtitle')}
        </p>
        <div className="mb-4">
          <a href={`/users/sign_up?locale=${i18n.language}`} className="w-full block bg-gradient-to-b from-orange-500 to-orange-600 text-white font-bold py-3.5 px-6 rounded-xl shadow-[0_4px_0_rgb(154,52,18)] hover:shadow-[0_2px_0_rgb(154,52,18)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all text-center text-lg">
            {t('landing.cta_signup_free')}
          </a>
          <p className="text-gray-500 text-xs text-center mt-2">{t('landing.cta_no_autobilling')}</p>
        </div>
      </section>

      {/* ===== DEMO SECTION ===== */}
      <section id="demo-section" className="mb-8">
        <div className="text-center mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">{t('landing.demo_title')}</h2>
          <p className="text-gray-400 text-sm">{t('landing.demo_subtitle')}</p>
        </div>
      </section>

      <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg mb-8">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-bold text-gray-400 mb-1">{t('filter_by_part')}</label>
            <div className="relative mb-4">
              <select
                value={selectedBodyPart}
                onChange={(e) => setSelectedBodyPart(e.target.value)}
                className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
              >
                <option value="脚">{t('body_parts.legs')}</option>
                <option value="腕">{t('body_parts.arms')}</option>
                <option value="肩">{t('body_parts.shoulders')}</option>
                <option value="背中">{t('body_parts.back')}</option>
                <option value="胸">{t('body_parts.chest')}</option>
                <option value="腹筋">{t('body_parts.abs')}</option>
                <option value="有酸素運動">{t('body_parts.cardio')}</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" fillRule="evenodd"></path></svg>
              </div>
            </div>

            <label className="block text-sm font-bold text-gray-400 mb-1">{t('select_exercise')}</label>

            <ExerciseSelector
              exercises={filteredExercises}
              selectedName={exercise}
              onSelect={(name) => setExercise(name)}
            />

            <div className="mt-2">
              <input
                type="text"
                value={exercise}
                readOnly
                className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors text-sm"
                placeholder={t('select_exercise_placeholder')}
              />
            </div>
          </div>


          {isRunning ? (
            <div className="mt-4 min-h-[500px]">
              <LiveTracker onSave={handleSaveRun} />
            </div>
          ) : isPlank ? (
            isPlankTimerRunning ? (
              <div className="flex flex-col items-center gap-6 py-8">
                <p className="text-gray-400 text-sm font-bold tracking-widest uppercase">プランク 計測中</p>
                <div className="text-7xl font-mono font-bold text-orange-500 tabular-nums">
                  {String(Math.floor(plankElapsed / 60)).padStart(2, '0')}:{String(plankElapsed % 60).padStart(2, '0')}
                </div>
                <button
                  type="button"
                  onClick={handlePlankStop}
                  className="flex items-center justify-center gap-3 bg-slate-100 hover:bg-white text-slate-900 px-10 py-4 rounded-full font-bold text-lg shadow-lg transition-all active:scale-95 whitespace-nowrap"
                >
                  <Square fill="currentColor" size={22} /> 停止して保存
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-sm font-bold text-gray-400">{t('weight_kg')}</label>
                      <button
                        type="button"
                        onClick={() => { setIsBodyweight(b => !b); setWeight(''); setPlankWeightError(false) }}
                        className={`text-xs font-bold px-2 py-0.5 rounded-full border transition-colors ${isBodyweight ? 'bg-orange-500 border-orange-500 text-black' : 'border-gray-600 text-gray-400 hover:border-orange-500 hover:text-orange-400'}`}
                      >{t('bodyweight')}</button>
                    </div>
                    {isBodyweight ? (
                      <div className="w-full bg-[#2c2c2e] border border-orange-500/50 rounded-lg p-3 text-orange-400 font-bold flex items-center justify-between">
                        <span>{t('bodyweight')}</span>
                        <span className="text-xs text-gray-500 font-normal">60kgで計算</span>
                      </div>
                    ) : (
                      <input
                        type="number"
                        value={weight}
                        onChange={(e) => { setWeight(e.target.value); setPlankWeightError(false) }}
                        className={`w-full bg-[#2c2c2e] border rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none transition-colors ${plankWeightError ? 'border-red-500 focus:border-red-500' : 'border-gray-700 focus:border-orange-500'}`}
                        placeholder="0"
                        step="0.5"
                      />
                    )}
                    {plankWeightError && (
                      <p className="text-red-400 text-xs mt-1">重量か自重を選択してください</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-400 mb-1">{t('date')}</label>
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                      className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors appearance-none text-left"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handlePlankStart}
                  className="w-full bg-rose-600 hover:bg-rose-500 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  スタート
                </button>
              </div>
            )
          ) : (
            <>
              {
                isCardio ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-bold text-gray-400 mb-1">{t('time_min')}</label>
                      <input
                        type="number"
                        value={timeMin}
                        onChange={(e) => setTimeMin(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                        placeholder="30"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-400 mb-1">{t('time_sec')}</label>
                      <input
                        type="number"
                        value={timeSec}
                        onChange={(e) => setTimeSec(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                        placeholder="00"
                      />
                    </div>

                    <div>
                      {isStairClimber ? (
                        <>
                          <label className="block text-sm font-bold text-gray-400 mb-1">{t('floors')}</label>
                          <input
                            type="number"
                            value={floors}
                            onChange={(e) => setFloors(e.target.value)}
                            className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                            placeholder="50"
                          />
                        </>
                      ) : (
                        <>
                          <label className="block text-sm font-bold text-gray-400 mb-1">{t('distance_km')}</label>
                          <input
                            type="number"
                            value={distance}
                            onChange={(e) => setDistance(e.target.value)}
                            className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                            placeholder="5.0"
                          />
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-bold text-gray-400">{t('weight_kg')}</label>
                        <button
                          type="button"
                          onClick={() => { setIsBodyweight(b => !b); setWeight('') }}
                          className={`text-xs font-bold px-2 py-0.5 rounded-full border transition-colors ${isBodyweight ? 'bg-orange-500 border-orange-500 text-black' : 'border-gray-600 text-gray-400 hover:border-orange-500 hover:text-orange-400'}`}
                        >{t('bodyweight')}</button>
                      </div>
                      {isBodyweight ? (
                        <div className="w-full bg-[#2c2c2e] border border-orange-500/50 rounded-lg p-3 text-orange-400 font-bold flex items-center justify-between">
                          <span>{t('bodyweight')}</span>
                          <span className="text-xs text-gray-500 font-normal">60kgで計算</span>
                        </div>
                      ) : (
                        <input
                          type="number"
                          value={weight}
                          onChange={(e) => setWeight(e.target.value)}
                          className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                          placeholder="50"
                        />
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-400 mb-1">{t('reps')}</label>
                      <input
                        type="number"
                        value={reps}
                        onChange={(e) => setReps(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                        placeholder="10"
                      />
                    </div>
                  </div>
                )
              }

              {
                isCardio ? (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-1">
                      <label className="block text-sm font-bold text-gray-400 mb-1">{t('calories')}</label>
                      <input
                        type="number"
                        value={calories}
                        onChange={(e) => setCalories(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                        placeholder="300"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-bold text-gray-400 mb-1">{t('date')}</label>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors appearance-none text-left"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4">
                    <div className="col-span-1">
                      <label className="block text-sm font-bold text-gray-400 mb-1">{t('sets')}</label>
                      <input
                        type="number"
                        value={sets}
                        onChange={(e) => setSets(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors"
                        placeholder="3"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm font-bold text-gray-400 mb-1">{t('date')}</label>
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        className="w-full bg-[#2c2c2e] border border-gray-700 rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors appearance-none text-left"
                      />
                    </div>
                  </div>
                )
              }

              <button type="submit" className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-black font-bold py-3 rounded-lg hover:opacity-90 transition-opacity flex justify-center items-center gap-2 mt-2">
                <span>+</span> {t('add_log')}
              </button>
            </>
          )}
        </form>
      </div >

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4 text-white">{t('logs_for_date')} ({dailyLogs.length})</h2>
        <div className="bg-[#1c1c1e] rounded-xl border border-gray-800 shadow-lg overflow-hidden">
          {dailyLogs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('no_logs_yet')}
            </div>
          ) : (
            <div className="divide-y divide-gray-800">
              {dailyLogs.map(log => {
                const exerciseObj = exercises.find(e => e.name === log.exercise || e.en_name === log.exercise)
                const bodyPart = exerciseObj ? exerciseObj.body_part : ''

                return (
                  <div key={log.id} className="p-4 hover:bg-[#252527] transition-colors relative group">
                    <button
                      onClick={() => deleteLog(log.id)}
                      className="absolute top-4 right-4 text-gray-600 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"></polyline>
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                      </svg>
                    </button>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-orange-500">
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6.5 6.5l11 11" />
                          <path d="M21 21l-1-1" />
                          <path d="M3 3l1 1" />
                          <path d="M18 22l4-4" />
                          <path d="M2 6l4-4" />
                          <path d="M3 10l7-7" />
                          <path d="M14 21l7-7" />
                        </svg>
                      </span>
                      <h3 className="font-bold text-lg text-white">
                        <span className="text-gray-400 text-sm mr-2">{getBodyPartLabel(bodyPart)}</span>
                        {log.exercise}
                      </h3>
                    </div>
                    <div className="flex justify-between items-end text-sm">
                      <div className="flex gap-8">
                        {bodyPart === '有酸素運動' ? (
                          <>
                            {log.time && (
                              <div>
                                <span className="text-gray-500 block text-xs mb-1">{t('time_label')}</span>
                                <span className="text-orange-500 font-bold">
                                  {Math.floor(log.time)}{t('unit_min')}
                                  {Math.round((log.time - Math.floor(log.time)) * 60) > 0 ? ` ${Math.round((log.time - Math.floor(log.time)) * 60)}${t('unit_sec')}` : ''}
                                </span>
                              </div>
                            )}
                            {log.distance && (
                              <div>
                                <span className="text-gray-500 block text-xs mb-1">{t('distance_label')}</span>
                                <span className="text-white font-bold">{log.distance}km</span>
                              </div>
                            )}
                            {log.floors && (
                              <div>
                                <span className="text-gray-500 block text-xs mb-1">{t('floors')}</span>
                                <span className="text-white font-bold">{log.floors}{t('unit_floors')}</span>
                              </div>
                            )}
                            {log.calories && (
                              <div>
                                <span className="text-gray-500 block text-xs mb-1">{t('calories')}</span>
                                <span className="text-white font-bold">{log.calories}</span>
                              </div>
                            )}
                          </>
                        ) : (log.exercise === 'プランク' || log.exercise === 'Plank') ? (
                          <>
                            <div>
                              <span className="text-gray-500 block text-xs mb-1">{t('weight_label')}</span>
                              <span className="text-orange-500 font-bold">
                                {log.is_bodyweight ? t('bodyweight') : (log.weight != null ? `${log.weight}kg` : '—')}
                              </span>
                              {log.is_bodyweight && <span className="text-gray-500 text-xs ml-1">(60kg)</span>}
                            </div>
                            {log.time != null && (
                              <div>
                                <span className="text-gray-500 block text-xs mb-1">{t('time_label')}</span>
                                <span className="text-orange-500 font-bold">
                                  {Math.floor(log.time)}{t('unit_min')}
                                  {Math.round((log.time - Math.floor(log.time)) * 60) > 0 ? ` ${Math.round((log.time - Math.floor(log.time)) * 60)}${t('unit_sec')}` : ''}
                                </span>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div>
                              <span className="text-gray-500 block text-xs mb-1">{t('weight_label')}</span>
                              <span className="text-orange-500 font-bold">
                                {log.is_bodyweight ? t('bodyweight') : `${log.weight}kg`}
                              </span>
                              {log.is_bodyweight && <span className="text-gray-500 text-xs ml-1">(60kg)</span>}
                            </div>
                            <div>
                              <span className="text-gray-500 block text-xs mb-1">{t('reps')}</span>
                              <span className="text-white font-bold">{log.reps}{t('unit_reps')}</span>
                            </div>
                            <div>
                              <span className="text-gray-500 block text-xs mb-1">{t('sets_label')}</span>
                              <span className="text-white font-bold">{log.sets}{t('unit_sets')}</span>
                            </div>
                          </>
                        )}
                      </div>
                      <div className="text-gray-600 text-xs">
                        {log.date}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </section>

      <section className="mb-8">
        <Calendar
          logs={apiLogs}
          selectedDate={date}
          onDateSelect={(d) => setDate(d)}
        />
      </section>

      <section className="mb-8 relative">
        <div className="border-t-2 border-red-500 my-8 pt-4 text-center">
          <span className="inline-flex items-center rounded-md bg-orange-400/10 px-2 py-1 text-xs font-medium text-orange-400 ring-1 ring-inset ring-orange-400/20">
            {t('paid_area_warning')}
          </span>
        </div>
        <h2 className="text-xl font-bold mb-4 text-white">{t('balance')} ({formattedDate})</h2>
        <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
          <BodyPartRadarChart logs={chartLogs} />
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-xl font-bold mb-4 text-white">{t('daily_volume')} ({formattedDate})</h2>
        <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
          <DailyVolumeChart data={(() => {
            const volumeByPart = {}
            dailyLogs.forEach(log => {
              const exerciseObj = exercises.find(e => e.name === log.exercise || e.en_name === log.exercise)
              const part = exerciseObj ? exerciseObj.body_part : 'Unknown'
              if (part === '有酸素運動') return

              const effectiveWeight = log.is_bodyweight ? 60 : parseFloat(log.weight)
              const repsVal = parseFloat(log.reps)
              const setsVal = parseFloat(log.sets)
              const isPlankLog = log.exercise === 'プランク' || log.exercise === 'Plank'

              if (isPlankLog && !isNaN(effectiveWeight) && log.time) {
                const volume = effectiveWeight * log.time
                if (!volumeByPart[part]) volumeByPart[part] = 0
                volumeByPart[part] += volume
              } else if (!isNaN(effectiveWeight) && !isNaN(repsVal) && !isNaN(setsVal)) {
                const volume = effectiveWeight * repsVal * setsVal
                if (!volumeByPart[part]) volumeByPart[part] = 0
                volumeByPart[part] += volume
              }
            })

            return Object.keys(volumeByPart).map(part => ({
              name: getBodyPartLabel(part),
              volume: volumeByPart[part]
            }))
          })()} />
        </div>
      </section>

      <section className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white">{t('period_comparison')} ({comparisonPeriod === 'week' ? t('week') : t('month')})</h2>
          <div className="flex bg-[#2c2c2e] rounded-lg p-1">
            <button
              onClick={() => setComparisonPeriod('week')}
              className={`px-4 py-1 rounded-md text-sm font-bold transition-colors ${comparisonPeriod === 'week'
                ? 'bg-orange-500 text-black'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              {t('week')}
            </button>
            <button
              onClick={() => setComparisonPeriod('month')}
              className={`px-4 py-1 rounded-md text-sm font-bold transition-colors ${comparisonPeriod === 'month'
                ? 'bg-orange-500 text-black'
                : 'text-gray-400 hover:text-white'
                }`}
            >
              {t('month')}
            </button>
          </div>
        </div>
        <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-800 shadow-lg">
          <PeriodComparisonChart logs={apiLogs} period={comparisonPeriod} />
        </div>
      </section>

      {/* ===== FEATURES SECTION ===== */}
      <section className="mb-16">
        <div className="max-w-sm mx-auto mb-10 px-4">
          <a href={`/users/sign_up?locale=${i18n.language}`} className="w-full block bg-gradient-to-b from-orange-500 to-orange-600 text-white font-bold py-3.5 px-6 rounded-xl shadow-[0_4px_0_rgb(154,52,18)] hover:shadow-[0_2px_0_rgb(154,52,18)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all text-center text-lg">
            {t('landing.cta_signup_free')}
          </a>
          <p className="text-gray-500 text-xs text-center mt-2">{t('landing.cta_no_autobilling')}</p>
        </div>
        <h2 className="text-2xl font-bold text-white text-center mb-8">{t('landing.features_title')}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-[#1c1c1e] p-5 rounded-xl border border-pink-500/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-pink-500/20 p-2.5 rounded-lg">
                <ImageIcon className="text-pink-400" size={22} />
              </div>
              <h3 className="text-white font-bold text-lg">{t('landing.feature_picker_title')}</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{t('landing.feature_picker_desc')}</p>
          </div>
          <div className="bg-[#1c1c1e] p-5 rounded-xl border border-orange-500/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-orange-500/20 p-2.5 rounded-lg">
                <Brain className="text-orange-400" size={22} />
              </div>
              <h3 className="text-white font-bold text-lg">{t('landing.feature_ai_title')}</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{t('landing.feature_ai_desc')}</p>
          </div>
          <div className="bg-[#1c1c1e] p-5 rounded-xl border border-purple-500/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-purple-500/20 p-2.5 rounded-lg">
                <Camera className="text-purple-400" size={22} />
              </div>
              <h3 className="text-white font-bold text-lg">{t('landing.feature_ocr_title')}</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{t('landing.feature_ocr_desc')}</p>
          </div>
          <div className="bg-[#1c1c1e] p-5 rounded-xl border border-blue-500/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-blue-500/20 p-2.5 rounded-lg">
                <MapPin className="text-blue-400" size={22} />
              </div>
              <h3 className="text-white font-bold text-lg">{t('landing.feature_gps_title')}</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{t('landing.feature_gps_desc')}</p>
          </div>
          <div className="bg-[#1c1c1e] p-5 rounded-xl border border-green-500/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-green-500/20 p-2.5 rounded-lg">
                <Search className="text-green-400" size={22} />
              </div>
              <h3 className="text-white font-bold text-lg">{t('landing.feature_protein_title')}</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{t('landing.feature_protein_desc')}</p>
          </div>
          <div className="bg-[#1c1c1e] p-5 rounded-xl border border-yellow-500/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-yellow-500/20 p-2.5 rounded-lg">
                <Cloud className="text-yellow-400" size={22} />
              </div>
              <h3 className="text-white font-bold text-lg">{t('landing.feature_cloud_title')}</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{t('landing.feature_cloud_desc')}</p>
          </div>
          <div className="bg-[#1c1c1e] p-5 rounded-xl border border-cyan-500/50">
            <div className="flex items-center gap-3 mb-3">
              <div className="bg-cyan-500/20 p-2.5 rounded-lg">
                <Monitor className="text-cyan-400" size={22} />
              </div>
              <h3 className="text-white font-bold text-lg">{t('landing.feature_web_title')}</h3>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">{t('landing.feature_web_desc')}</p>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="mb-12">
        <h2 className="text-2xl font-bold text-white text-center mb-8">{t('landing.how_it_works_title')}</h2>
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 bg-[#1c1c1e] p-5 rounded-xl border border-gray-800 text-center">
            <div className="bg-orange-500/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <Dumbbell className="text-orange-400" size={22} />
            </div>
            <h3 className="text-white font-bold mb-1">{t('landing.step1_title')}</h3>
            <p className="text-gray-400 text-sm">{t('landing.step1_desc')}</p>
          </div>
          <div className="flex items-center justify-center text-gray-600 md:rotate-0 rotate-90">
            <ChevronDown size={24} />
          </div>
          <div className="flex-1 bg-[#1c1c1e] p-5 rounded-xl border border-gray-800 text-center">
            <div className="bg-orange-500/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <Timer className="text-orange-400" size={22} />
            </div>
            <h3 className="text-white font-bold mb-1">{t('landing.step2_title')}</h3>
            <p className="text-gray-400 text-sm">{t('landing.step2_desc')}</p>
          </div>
          <div className="flex items-center justify-center text-gray-600 md:rotate-0 rotate-90">
            <ChevronDown size={24} />
          </div>
          <div className="flex-1 bg-[#1c1c1e] p-5 rounded-xl border border-gray-800 text-center">
            <div className="bg-green-500/20 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
              <Check className="text-green-400" size={22} />
            </div>
            <h3 className="text-white font-bold mb-1">{t('landing.step3_title')}</h3>
            <p className="text-gray-400 text-sm">{t('landing.step3_desc')}</p>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl font-bold mb-6 text-white text-center">{t('pricing_title')}</h2>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">

          {/* お試し会員プラン */}
          <div className="bg-gradient-to-b from-orange-400/20 to-[#1c1c1e] p-6 rounded-xl border-2 border-orange-500 shadow-lg shadow-orange-900/40 flex flex-col relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-orange-500 text-black font-bold text-xs px-2 py-1 rounded-bl-lg">
              {t('recommended_badge')}
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">{t('trial_plan_name')}</h3>
            <p className="text-orange-400 font-bold text-lg mb-6">{t('trial_plan_price')}</p>

            <div className="flex-grow">
              <p className="text-gray-400 text-sm font-bold mb-3 uppercase tracking-wider">{t('features_label')}</p>
              <ul className="space-y-3">
                {[
                  'feature_logs', 'feature_calendar', 'feature_share_logs',
                  'feature_protein_search', 'feature_gps',
                  'feature_balance', 'feature_volume', 'feature_comparison',
                  'feature_three_d_model', 'feature_impressions',
                  'feature_ai_coach_unlimited', 'feature_image_import_unlimited',
                  'feature_year_trend', 'feature_monthly_summary'
                ].map(key => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="bg-orange-500/20 p-1 rounded-full text-orange-400 mt-0.5">
                      <Check size={16} strokeWidth={3} />
                    </span>
                    <span className="text-white font-bold">{t(key)}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-6">
              <a href={`/users/sign_up?locale=${i18n.language}`} className="w-full block bg-gradient-to-b from-orange-500 to-orange-600 text-white font-bold py-3.5 px-6 rounded-xl shadow-[0_4px_0_rgb(154,52,18)] hover:shadow-[0_2px_0_rgb(154,52,18)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all text-center text-lg">
                {t('landing.cta_signup_free')}
              </a>
              <p className="text-gray-500 text-xs text-center mt-2">{t('landing.cta_no_autobilling')}</p>
            </div>
          </div>

          {/* 無料会員プラン */}
          <div className="bg-[#1c1c1e] p-6 rounded-xl border border-gray-400 shadow-lg shadow-gray-200/10 flex flex-col relative overflow-hidden">
            <h3 className="text-2xl font-bold text-white mb-2">{t('free_plan_name')}</h3>
            <p className="text-gray-300 font-bold text-lg mb-6">{t('free_plan_price')}</p>

            <div className="flex-grow">
              <p className="text-gray-400 text-sm font-bold mb-3 uppercase tracking-wider">{t('features_label')}</p>
              <ul className="space-y-3">
                {[
                  'feature_logs', 'feature_calendar', 'feature_share_logs',
                  'feature_protein_search', 'feature_gps', 'feature_impressions',
                  'feature_ai_coach_1time', 'feature_image_import_3'
                ].map(key => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="bg-gray-700 p-1 rounded-full text-gray-300 mt-0.5">
                      <Check size={16} strokeWidth={3} />
                    </span>
                    <span className="text-gray-200">{t(key)}</span>
                  </li>
                ))}
                {[
                  'feature_balance', 'feature_volume', 'feature_comparison',
                  'feature_three_d_model',
                  'feature_ai_coach_unlimited', 'feature_image_import_unlimited',
                  'feature_year_trend', 'feature_monthly_summary'
                ].map(key => (
                  <li key={key} className="flex items-start gap-3 opacity-60">
                    <span className="p-1 mt-0.5">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </span>
                    <span className="text-gray-500 line-through">{t(key)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-orange-300 text-sm font-bold mt-6 leading-relaxed">{t('free_plan_continue_note')}</p>
            </div>
          </div>

          {/* ベーシック会員プラン */}
          <div className="bg-[#1c1c1e] p-6 rounded-xl border-2 border-blue-500 shadow-lg shadow-blue-900/20 flex flex-col relative overflow-hidden">
            <h3 className="text-2xl font-bold text-white mb-2">{t('basic_plan_name')}</h3>
            <p className="text-blue-400 font-bold text-lg mb-6">{t('basic_plan_price')}</p>

            <div className="flex-grow">
              <p className="text-gray-400 text-sm font-bold mb-3 uppercase tracking-wider">{t('features_label')}</p>
              <ul className="space-y-3">
                {[
                  'feature_logs', 'feature_calendar', 'feature_share_logs',
                  'feature_protein_search', 'feature_gps',
                  'feature_balance', 'feature_volume', 'feature_comparison',
                  'feature_three_d_model', 'feature_impressions',
                ].map(key => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="bg-blue-500/10 p-1 rounded-full text-blue-400 mt-0.5">
                      <Check size={16} strokeWidth={3} />
                    </span>
                    <span className="text-white font-bold">{t(key)}</span>
                  </li>
                ))}
                {[
                  'feature_ai_coach_5times',
                  'feature_image_import_10',
                ].map(key => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="bg-blue-500/10 p-1 rounded-full text-blue-400 mt-0.5">
                      <Check size={16} strokeWidth={3} />
                    </span>
                    <span className="text-white font-bold">
                      {t(key).split(/([（(].+[）)])/).map((part, i) =>
                        i % 2 === 1
                          ? <span key={i} className="text-blue-300 font-bold">{part}</span>
                          : part
                      )}
                    </span>
                  </li>
                ))}
                {['feature_year_trend', 'feature_monthly_summary'].map(key => (
                  <li key={key} className="flex items-start gap-3 opacity-60">
                    <span className="p-1 mt-0.5">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </span>
                    <span className="text-gray-500 line-through">{t(key)}</span>
                  </li>
                ))}
              </ul>
              <p className="text-blue-300 text-sm font-bold mt-6 leading-relaxed">{t('basic_plan_no_auto_charge_note')}</p>
            </div>
          </div>

          {/* プレミアム会員プラン */}
          <div className="bg-[#1c1c1e] p-6 rounded-xl border-2 border-purple-500 shadow-lg shadow-purple-900/20 flex flex-col relative overflow-hidden">
            <h3 className="text-2xl font-bold text-white mb-2">{t('paid_plan_name')}</h3>
            <p className="text-purple-400 font-bold text-lg mb-6">{t('paid_plan_price')}</p>

            <div className="flex-grow">
              <p className="text-gray-400 text-sm font-bold mb-3 uppercase tracking-wider">{t('features_label')}</p>
              <ul className="space-y-3">
                {[
                  'feature_logs', 'feature_calendar', 'feature_share_logs',
                  'feature_protein_search', 'feature_gps',
                  'feature_balance', 'feature_volume', 'feature_comparison',
                  'feature_three_d_model', 'feature_impressions',
                ].map(key => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="bg-purple-500/10 p-1 rounded-full text-purple-400 mt-0.5">
                      <Check size={16} strokeWidth={3} />
                    </span>
                    <span className="text-white font-bold">{t(key)}</span>
                  </li>
                ))}
                {[
                  'feature_ai_coach_unlimited',
                  'feature_image_import_unlimited',
                  'feature_year_trend',
                  'feature_monthly_summary',
                ].map(key => (
                  <li key={key} className="flex items-start gap-3">
                    <span className="bg-purple-500/10 p-1 rounded-full text-purple-400 mt-0.5">
                      <Check size={16} strokeWidth={3} />
                    </span>
                    <span className="text-white font-bold">
                      {t(key).split(/([（(].+[）)])/).map((part, i) =>
                        i % 2 === 1
                          ? <span key={i} className="text-purple-300 font-bold">{part}</span>
                          : part
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-purple-300 text-sm font-bold mt-6 leading-relaxed">{t('paid_plan_no_auto_charge_note')}</p>
            </div>
          </div>

        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-xl font-bold mb-6 text-white text-center">{t('faq_title')}</h2>
        <div className="bg-[#1c1c1e] rounded-xl border border-gray-800 shadow-lg divide-y divide-gray-800">
          {[
            {
              q: t('faq_q1'),
              a: (
                <div className="space-y-4">
                  <p className="text-gray-300 leading-relaxed">{t('faq_a1')}</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                    <div className="flex flex-col">
                      <p className="text-white font-bold mb-3">{t('faq_iphone_example')}</p>
                      <div className="bg-white/5 rounded-xl p-3 border border-white/10 space-y-3 mb-4">
                        <div className="flex items-center gap-3 text-xs text-gray-300">
                          <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0 border border-blue-500/20">
                            <Share size={14} className="text-blue-400" />
                          </div>
                          <p>1. {t('pwa.ios_instruction').split('「')[0]}</p>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-300">
                          <div className="w-7 h-7 rounded-lg bg-gray-500/20 flex items-center justify-center shrink-0 border border-gray-500/20">
                            <ChevronDown size={14} />
                          </div>
                          <p>2. {t('pwa.ios_step2')}</p>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-300">
                          <div className="w-7 h-7 rounded-lg bg-orange-500/20 flex items-center justify-center shrink-0 border border-orange-500/20">
                            <PlusSquare size={14} className="text-orange-400" />
                          </div>
                          <p>3. {t('pwa.ios_step3')}</p>
                        </div>
                      </div>
                      <img src="/iphone-pwa.jpg" alt="iPhone PWA setup example" className="rounded-lg border border-gray-700 w-full max-w-sm mx-auto" />
                    </div>
                    <div className="flex flex-col">
                      <p className="text-white font-bold mb-3">{t('faq_android_example')}</p>
                      <div className="bg-white/5 rounded-xl p-3 border border-white/10 space-y-3 mb-4">
                        <div className="flex items-start gap-3 text-xs text-gray-300">
                          <div className="w-7 h-7 rounded-lg bg-gray-500/20 flex items-center justify-center shrink-0 border border-gray-500/20 mt-0.5">
                            <Info size={14} className="text-gray-400" />
                          </div>
                          <p className="leading-relaxed">{t('pwa.android_instruction')}</p>
                        </div>
                      </div>
                      <img src="/android-pwa.jpg" alt="Android PWA setup example" className="rounded-lg border border-gray-700 w-full max-w-sm mx-auto" />
                    </div>
                  </div>
                </div>
              ),
            },
            {
              q: t('faq_q5'),
              a: (
                <div className="space-y-4">
                  <p className="text-gray-300 leading-relaxed">{t('faq_a5')}</p>
                </div>
              ),
            },
            {
              q: t('faq_q4'),
              a: (
                <div className="space-y-4">
                  <p className="text-gray-300 leading-relaxed">{t('faq_a4')}</p>
                  <div className="flex flex-col items-center mt-4">
                    <img src="/history-button.jpg" alt="History button example" className="rounded-lg border border-gray-700 w-full max-w-sm mx-auto" />
                  </div>
                </div>
              ),
            },
            {
              q: t('faq_q6'),
              a: (
                <div className="space-y-4">
                  <p className="text-gray-300 leading-relaxed">{t('faq_a6')}</p>
                </div>
              ),
            },
            {
              q: t('faq_q2'),
              a: (
                <div className="space-y-4">
                  <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{t('faq_a2')}</p>
                  <div className="flex flex-col items-center mt-4">
                    <p className="text-white font-bold mb-2">{t('faq_import_example')}</p>
                    <img src="/import-demo.jpg" alt="Import record example" className="rounded-lg border border-gray-700 w-full max-w-sm mx-auto" />
                  </div>
                </div>
              ),
            },
            {
              q: t('faq_q3'),
              a: (
                <div className="space-y-4">
                  <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{t('faq_a3')}</p>
                  <div className="flex flex-col gap-6 mt-4">
                    <img src="/running-image01.jpg" alt="Running tracker example 1" className="rounded-lg border border-gray-700 w-full max-w-sm mx-auto shadow-lg" />
                    <img src="/running-image02.jpg" alt="Running tracker example 2" className="rounded-lg border border-gray-700 w-full max-w-sm mx-auto shadow-lg" />
                  </div>
                </div>
              ),
            },
          ].map((item, idx) => {
            const open = openFaq === idx
            return (
              <div key={idx} className="p-6">
                <button
                  className="flex items-start gap-4 w-full text-left"
                  onClick={() => setOpenFaq(open ? null : idx)}
                >
                  <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-orange-500 text-black font-bold">Q</span>
                  <span className="text-white font-bold text-lg pt-1 flex-1">{item.q}</span>
                  <ChevronDown size={20} className={`flex-shrink-0 mt-1.5 text-gray-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                </button>
                {open && (
                  <div className="flex gap-4 mt-4">
                    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-red-500 text-white font-bold">A</span>
                    <div className="w-full">{item.a}</div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </section>



      <section className="mb-16 mt-8">
        <div className="bg-[#1c1c1e] p-8 rounded-xl border border-orange-500/30 shadow-lg text-center space-y-6">
          <h2 className="text-2xl font-bold text-white italic">
            {t('hero_message_1')}
          </h2>
          <p className="text-gray-400">
            {t('hero_message_2')}
          </p>
          <div className="max-w-sm mx-auto">
            <a href={`/users/sign_up?locale=${i18n.language}`} className="w-full block bg-gradient-to-b from-orange-500 to-orange-600 text-white font-bold py-3.5 px-6 rounded-xl shadow-[0_4px_0_rgb(154,52,18)] hover:shadow-[0_2px_0_rgb(154,52,18)] hover:translate-y-[2px] active:shadow-none active:translate-y-[4px] transition-all text-lg">
              {t('landing.cta_signup_free')}
            </a>
            <p className="text-gray-500 text-xs text-center mt-2">{t('landing.cta_no_autobilling')}</p>
          </div>
        </div>
      </section>

      <Footer />

    </div >
  )
}

export default LandingPage
